/**
 * Pulls the applied query parameters out of a reply.
 *
 * Every agent response ends with a fenced block recording what the retrieval
 * actually ran with — modules, brand ids, markets, period, segments, filter
 * codes. It is the honest answer to "what did Lou look at", and it is not the
 * same as the screen context: the agent resolves names to ids, picks filters
 * of its own, and may widen a period to get a usable base.
 *
 * See lou-response-metadata-v1.md. Three shapes are accepted, because they
 * ship at different times and stored turns keep the shape they were written
 * with:
 *
 *   ```response-metadata   [ {...}, {...} ]   ← the contract
 *   ```response-metadata   { ... }            ← early build, not yet wrapped
 *   ```json                { ... }            ← today, before the fence exists
 *
 * All three normalise to a list of retrievals.
 */

/** Keys that mark a JSON block as applied parameters rather than content. */
const MARKERS = [
  "modules",
  "module",
  "brands",
  "brand_id",
  "markets",
  "market_id",
  "period",
  "time_period",
  "segments",
  "filter_codes",
  "filters",
];

const METADATA_FENCE = /```response-metadata\s*\n([\s\S]*?)```/g;
const JSON_FENCE = /```json\s*\n([\s\S]*?)```/g;

export const looksLikeParams = (data) =>
  Boolean(data) &&
  typeof data === "object" &&
  !Array.isArray(data) &&
  MARKERS.some((key) => key in data);

/** An entry, a bare object, or a list of either — always out as a list. */
const asRetrievals = (parsed) => {
  if (Array.isArray(parsed)) return parsed.filter(looksLikeParams);
  return looksLikeParams(parsed) ? [parsed] : [];
};

/** The last fence in `text` whose contents are applied parameters. */
const lastMatching = (text, pattern) => {
  let found = null;
  let match = pattern.exec(text);
  while (match !== null) {
    try {
      const retrievals = asRetrievals(JSON.parse(match[1]));
      // Last one wins: the block is documented as trailing, and anything
      // earlier is part of the answer.
      if (retrievals.length) found = retrievals;
    } catch {
      // A block that will not parse is not the one we are looking for.
    }
    match = pattern.exec(text);
  }
  pattern.lastIndex = 0;
  return found;
};

/**
 * Every retrieval behind a reply, or `null` when it has none.
 *
 * A reply still streaming has no closing fence yet and returns null, which is
 * correct: there is nothing to show until it lands.
 */
export const extractAppliedParams = (text) => {
  if (typeof text !== "string") return null;
  // The named fence is authoritative. The plain one is only consulted when a
  // reply has no named block at all, so an old turn still renders.
  return (
    lastMatching(text, METADATA_FENCE) ?? lastMatching(text, JSON_FENCE) ?? null
  );
};

/** Ids can arrive singular or plural, scalar or array. */
const ids = (...candidates) => {
  for (const value of candidates) {
    if (value === undefined || value === null) continue;
    const list = Array.isArray(value) ? value : [value];
    if (list.length) return list;
  }
  return [];
};

/** `{preset, start, end}` in either spelling, or nulls when absent. */
const asPeriod = (period) => {
  if (!period || typeof period !== "object") return null;
  const type = period.preset ?? period.type ?? "";
  const startDate = period.start ?? period.start_date ?? "";
  const endDate = period.end ?? period.end_date ?? "";
  return type || startDate ? { type, startDate, endDate } : null;
};

/** One retrieval in the shape the panel reads. */
const normaliseOne = (params) => {
  const period = params.time_period ?? params.period ?? {};
  return {
    // What this query was measured against, when it named one. Not derivable
    // from anything else: a reply comparing to the prior 90 days and one
    // comparing to last year look identical without it.
    comparisonPeriod: asPeriod(params.comparison_period),
    modules: ids(params.modules, params.module),
    screen: params.screen ?? "",
    brands: ids(params.brands, params.brand_id),
    competitors: ids(params.competitors),
    markets: ids(params.markets, params.market_id),
    segments: ids(params.segments),
    filters: ids(params.filters, params.filter_codes).map((entry) =>
      // Contract shape is `{code, kind, options}`; the older block sent bare
      // codes in `filter_codes`.
      typeof entry === "string" ? { code: entry } : entry,
    ),
    audienceBase: params.audience_base ?? "",
    statTest: params.stat_test ?? "",
    analysisMode: params.analysis_mode ?? "",
    interval: params.interval ?? "",
    period: {
      type: period.preset ?? period.type ?? "",
      startDate: period.start ?? period.start_date ?? "",
      endDate: period.end ?? period.end_date ?? "",
    },
  };
};

/** Unique, order-preserving. */
const unique = (values) => [...new Set(values.map(String))];

/**
 * The retrievals behind a reply, plus what every one of them had in common.
 *
 * The panel shows the common part once and the rest per retrieval, so the
 * split is computed here rather than asked of the sender — the payload stays
 * self-describing and the display decision stays in the UI.
 */
export const normaliseAppliedParams = (retrievals) => {
  if (!retrievals?.length) return null;
  const all = retrievals.map(normaliseOne);

  const sameEverywhere = (read) => {
    const first = JSON.stringify(read(all[0]));
    return all.every((one) => JSON.stringify(read(one)) === first);
  };

  return {
    retrievals: all,
    // Every id seen anywhere, for the sections that list entities.
    modules: unique(all.flatMap((one) => one.modules)),
    brands: unique(all.flatMap((one) => one.brands)),
    competitors: unique(all.flatMap((one) => one.competitors)),
    markets: unique(all.flatMap((one) => one.markets)),
    segments: unique(all.flatMap((one) => one.segments)),
    filters: all.flatMap((one) => one.filters),
    // Settings are only stated when they hold for the whole answer. One
    // retrieval on Gen Pop and another on Familiar must not read as either.
    screen: sameEverywhere((one) => one.screen) ? all[0].screen : "",
    audienceBase: sameEverywhere((one) => one.audienceBase) ? all[0].audienceBase : "",
    statTest: sameEverywhere((one) => one.statTest) ? all[0].statTest : "",
    analysisMode: sameEverywhere((one) => one.analysisMode) ? all[0].analysisMode : "",
    interval: sameEverywhere((one) => one.interval) ? all[0].interval : "",
    period: sameEverywhere((one) => one.period) ? all[0].period : null,
  };
};

export default extractAppliedParams;
